/**
 * Formatting helpers and label mappings for billing order views.
 *
 * @see AC-1c in docs/specifications/2026-04-22-admin-order-creation-ui/spec.md
 */

// Re-export shared label module (single source of truth — see C2/M4 in
// docs/specifications/2026-05-12-admin-manual-pricing-ui/reviews/consolidated.md).
export { PRICING_TYPE_LABELS } from '../../../../types/billing-labels';

// =============================================================================
// FORMAT HELPERS
// =============================================================================

/** Format cents as a PLN currency string (e.g. 12345 -> "123,45 PLN") */
export const formatCents = (cents: number | null): string => {
  if (cents == null) return "—";
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    currencyDisplay: "code",
  }).format(cents / 100);
};

/**
 * Format cents as a PLN currency string with explicit 2-decimal precision
 * (e.g. 12300 -> "123,00 PLN"). Use when showing precise current price values
 * for context (e.g. "current price" display in manual pricing modal).
 */
export const formatCentsWithDecimals = (cents: number | null): string => {
  if (cents == null) return "—";
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
};

/** Format an ISO date string or undefined/null as a localized Polish date */
export const formatDate = (date?: string | null): string => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("pl-PL");
};

/** Returns today as YYYY-MM-DD string for date input default */
export const todayIso = (): string => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

/**
 * "Today" as a YYYY-MM-DD calendar day in Europe/Warsaw — the day-boundary
 * contract for the proforma due-date feature (AC-3). Unlike `todayIso()`
 * (browser-local/UTC), this always resolves the calendar day an admin in
 * Poland would call "today", including across DST transitions (handled by
 * `Intl.DateTimeFormat`, never by adding/subtracting a fixed UTC offset).
 */
export const todayInWarsaw = (referenceDate: Date = new Date()): string => {
  // en-CA locale formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Warsaw",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(referenceDate);
};

/**
 * Adds `days` calendar days to a YYYY-MM-DD date-only string using UTC
 * arithmetic on the calendar components (no timezone conversion) — safe to
 * combine with `todayInWarsaw()` without re-introducing a local-timezone bug.
 */
export const addDaysToIsoDate = (isoDate: string, days: number): string => {
  const [year, month, day] = isoDate.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/** Format cents to PLN display string (e.g. 12345 -> "123.45") */
export const centsToPlnString = (cents: number): string =>
  (cents / 100).toFixed(2);

/** Parse PLN string to cents integer */
export const plnToCents = (pln: string): number =>
  Math.round(parseFloat(pln) * 100);

// =============================================================================
// LABEL MAPPINGS (Polish translations for enum values — AC-1c)
// =============================================================================

export const DOC_TYPE_LABELS: Record<string, string> = {
  PROFORMA: "Proforma",
  INVOICE: "Faktura VAT",
  VAT_INVOICE: "Faktura VAT",
};

export const DOC_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Szkic",
  ISSUED: "Wystawiony",
  PAID: "Opłacony",
  CANCELED: "Anulowany",
};

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  BANK_TRANSFER: "Przelew bankowy",
  STRIPE: "Karta płatnicza",
};

